/** Display helpers for the member's paged private conversation. */

import type { ChatMessage, ChatThread } from "~/types/chat";

/**
 * Merge offset pages defensively. New replies can shift boundaries between polls, so
 * an item can briefly exist on two loaded pages; offset zero is the freshest copy.
 */
export function uniqueChatMessages(
  pages: ReadonlyArray<Pick<ChatThread, "messages">>,
): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  for (const page of pages) {
    for (const message of page.messages) {
      if (!byId.has(message.id)) byId.set(message.id, message);
    }
  }
  return [...byId.values()].sort(
    (left, right) =>
      left.created_at.localeCompare(right.created_at) ||
      left.id.localeCompare(right.id),
  );
}

/** One row of the transcript: a stored message, or the app's own confirmation. */
export type ChatTranscriptItem =
  | { kind: "message"; message: ChatMessage }
  | { kind: "auto-reply"; id: string; created_at: string };

/**
 * Close every run of member messages with an automatic confirmation.
 *
 * The chat is not live, so the member is told their message has landed and that the
 * answer will arrive here. The confirmation is derived from the history, not stored:
 * it lasts exactly as long as the messages it follows, on every device and in the
 * member's current language, and the team's inbox, unread counts and reply-email rule
 * never have to learn to skip a message nobody wrote. It takes the time of the member
 * message it answers, and appears only once the API has accepted that message.
 */
export function withAutoReplies(
  messages: ReadonlyArray<ChatMessage>,
): ChatTranscriptItem[] {
  return messages.flatMap((message, index): ChatTranscriptItem[] => {
    const item = { kind: "message" as const, message };
    const closesMemberRun =
      message.sender_role === "member" &&
      messages[index + 1]?.sender_role !== "member";
    if (!closesMemberRun) return [item];
    return [
      item,
      {
        kind: "auto-reply",
        id: `auto-reply-${message.id}`,
        created_at: message.created_at,
      },
    ];
  });
}
